
import React from 'react';
import { Button, Modal, Table, Form } from 'react-bootstrap';
import ShipmentApi from '../../api/ShipmentApi';
import AuthorizedPage from '../../component/AuthorizedPage';
import TruckFormation, { FormationPoint } from '../../component/TruckFormation';

type VehicleStatus = 'PARKING' | 'AVAILABLE' | 'LOAD_WEIGHT_CHECK' | 'ON_DELIVERY' | 'ON_VACATION';

interface Vehicle {
  id?: number;
  brand: string;
  model: string;
  licencePlateCode: string;
  productionYear: number;
  yearOfPurchase: number;
  capacityInTons: number;
  palletCapacity: number;
  drivers: string;
  fuelConsumptionEmpty?: number;
  fuelConsumptionHalfFull?: number;
  fuelConsumptionFull?: number;
  statusDetail?: { name: VehicleStatus; label: string };
  formationName?: string;
  formationId?: number;
}

interface State {
  vehicles: Vehicle[];
  showModal: boolean;
  editingVehicle: Vehicle | null;
  formations: { formationId: number; formationName: string }[];
  showFormationModal: boolean;
  formationPoints: FormationPoint[];
  selectedLayer?: number; // <-- bunu ekle
}

const defaultVehicle: Vehicle = {
  brand: '',
  model: '',
  licencePlateCode: '',
  productionYear: new Date().getFullYear(),
  yearOfPurchase: new Date().getFullYear(),
  capacityInTons: 0,
  palletCapacity: 0,
  drivers: '',
  fuelConsumptionEmpty: 0,
  fuelConsumptionHalfFull: 0,
  fuelConsumptionFull: 0,
  formationName: ''
};

export default class VehicleManagement extends React.Component<{}, State> {
  state: State = {
    vehicles: [],
    showModal: false,
    editingVehicle: null,
    formations: [],
    showFormationModal: false,
    formationPoints: [],
  };

  componentDidMount() {
    this.loadVehicles();
    this.loadFormations();
  }

  loadVehicles = async () => {
    const result = await ShipmentApi.listVehicles();
    this.setState({ vehicles: result.success && result.data ? result.data : [] });
  };

  loadFormations = async () => {
    const result = await ShipmentApi.listFormations();
    this.setState({ formations: result.success && result.data ? result.data : [] });
  };

  loadFormationPoints = async (vehicleId: number) => {
    const result = await ShipmentApi.listVehicleFormationPoints(vehicleId);
    const points = result.success && result.data ? result.data : [];
    this.setState({
      formationPoints: points,
      selectedLayer: points.length > 0 ? points[0].layer : 1, // default seçili layer
      showFormationModal: true
    });
  };


  openEditModal = (vehicle?: Vehicle) => {
    this.setState({
      editingVehicle: vehicle ? { ...vehicle } : { ...defaultVehicle },
      showModal: true
    });
  };

  handleChange = (e: React.ChangeEvent<any>) => {
    if (!this.state.editingVehicle) return;
    const { name, value } = e.target;

    this.setState(prev => {
      const updatedVehicle = { ...prev.editingVehicle! };
      if (name === 'formationId') {
        const id = parseInt(value);
        updatedVehicle.formationId = id;
        updatedVehicle.formationName = prev.formations.find(f => f.formationId === id)?.formationName || '';
      } else if (
        ['productionYear', 'yearOfPurchase', 'capacityInTons', 'palletCapacity', 'fuelConsumptionEmpty', 'fuelConsumptionHalfFull', 'fuelConsumptionFull'].includes(name)
      ) {
        (updatedVehicle as any)[name] = parseFloat(value) || 0;
      } else {
        (updatedVehicle as any)[name] = value;
      }
      return { editingVehicle: updatedVehicle };
    });
  };

  handleSave = async () => {
    const { editingVehicle } = this.state;
    if (!editingVehicle) return;
    await ShipmentApi.createOrUpdateVehicle(editingVehicle);
    this.setState({ showModal: false, editingVehicle: null }, this.loadVehicles);
  };

  renderVehicleTable() {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Plaka</th>
            <th>Marka</th>
            <th>Model</th>
            <th>Üretim Yılı</th>
            <th>Satın Alma Yılı</th>
            <th>Kapasite (ton)</th>
            <th>Palet Kapasitesi</th>
            <th>Formasyon</th>
            <th>Boş (lt/100km)</th>
            <th>Yarı Dolu (lt/100km)</th>
            <th>Full (lt/100km)</th>
            <th>Durum</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {this.state.vehicles.map(v => (
            <tr key={v.id}>
              <td>{v.licencePlateCode}</td>
              <td>{v.brand}</td>
              <td>{v.model}</td>
              <td>{v.productionYear}</td>
              <td>{v.yearOfPurchase}</td>
              <td>{v.capacityInTons}</td>
              <td>{v.palletCapacity}</td>
              <td>
                {v.formationName ?? '-'}{' '}
                {v.formationId && <Button size="sm" onClick={() => this.loadFormationPoints(v.id!)}>Göster</Button>}
              </td>
              <td>{v.fuelConsumptionEmpty}</td>
              <td>{v.fuelConsumptionHalfFull}</td>
              <td>{v.fuelConsumptionFull}</td>
              <td>{v.statusDetail?.label}</td>
              <td><Button size="sm" onClick={() => this.openEditModal(v)}>Düzenle</Button></td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderVehicleModal() {
    const { editingVehicle } = this.state;
    if (!editingVehicle) return null;

    return (
      <Modal show={this.state.showModal} onHide={() => this.setState({ showModal: false })}>
        <Modal.Header closeButton>
          <Modal.Title>Araç Bilgileri</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Plaka</Form.Label>
              <Form.Control name="licencePlateCode" value={editingVehicle.licencePlateCode} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Marka</Form.Label>
              <Form.Control name="brand" value={editingVehicle.brand} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Model</Form.Label>
              <Form.Control name="model" value={editingVehicle.model} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Üretim Yılı</Form.Label>
              <Form.Control type="number" name="productionYear" value={editingVehicle.productionYear} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Satın Alma Yılı</Form.Label>
              <Form.Control type="number" name="yearOfPurchase" value={editingVehicle.yearOfPurchase} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Kapasite (ton)</Form.Label>
              <Form.Control type="number" name="capacityInTons" value={editingVehicle.capacityInTons} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Palet Kapasitesi</Form.Label>
              <Form.Control type="number" name="palletCapacity" value={editingVehicle.palletCapacity} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Formasyon</Form.Label>
              <Form.Select
                name="formationId"
                value={editingVehicle.formationId || ''}
                onChange={this.handleChange}
              >
                <option value="">Seçiniz</option>
                {this.state.formations.map(f => (
                  <option key={f.formationId} value={f.formationId}>
                    {f.formationName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Boş (lt/100km)</Form.Label>
              <Form.Control type="number" name="fuelConsumptionEmpty" value={editingVehicle.fuelConsumptionEmpty} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Yarı Dolu (lt/100km)</Form.Label>
              <Form.Control type="number" name="fuelConsumptionHalfFull" value={editingVehicle.fuelConsumptionHalfFull} onChange={this.handleChange}/>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Full (lt/100km)</Form.Label>
              <Form.Control type="number" name="fuelConsumptionFull" value={editingVehicle.fuelConsumptionFull} onChange={this.handleChange}/>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showModal: false })}>İptal</Button>
          <Button variant="primary" onClick={this.handleSave}>Kaydet</Button>
        </Modal.Footer>
      </Modal>
    );
  }


  renderFormationModal() {
    const { formationPoints, showFormationModal } = this.state;

    // Mevcut layer'lar backend'den gelen veriye göre otomatik bulunur
    const availableLayers = Array.from(new Set(formationPoints.map(p => p.layer))).sort();

    return (
      <Modal
        show={showFormationModal}
        onHide={() => this.setState({ showFormationModal: false })}
        size="lg"
        dialogClassName="formation-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Araç Palet Dizilimi</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ height: '80vh', padding: '1rem', overflowY: 'auto' }}>
          {formationPoints.length === 0 ? (
            <p>Formasyon bilgisi bulunamadı.</p>
          ) : (
            <>
              {/* Kat seçim dropdown */}
              <div style={{ marginBottom: '1rem' }}>
                <label>Kat Seçimi: </label>
                <select
                  value={this.state.selectedLayer || 1}
                  onChange={e => this.setState({ selectedLayer: parseInt(e.target.value) })}
                  style={{ marginLeft: '0.5rem' }}
                >
                  {availableLayers.map(l => (
                    <option key={l} value={l}>{l}. Kat</option>
                  ))}
                </select>
              </div>

              {/* Seçili layer render */}
              <TruckFormation
                formationPoints={formationPoints}
                layer={this.state.selectedLayer || 1}
                gap={5}
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showFormationModal: false })}>Kapat</Button>
        </Modal.Footer>
      </Modal>
    );
  }



  render() {
    return (
      <AuthorizedPage role="ORDER_MANAGER" isLoading={false}>
        <div className="container mt-4">
          <h4>Araç Yönetimi</h4>
          <Button className="mb-3" onClick={() => this.openEditModal()}>+ Araç Ekle</Button>
          {this.renderVehicleTable()}
          {this.renderVehicleModal()}
          {this.renderFormationModal()}
        </div>
      </AuthorizedPage>
    );
  }
}
